import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { AlertTriangle, Settings2 } from "lucide-react";
import WidgetGridBuilder from "@/components/WidgetGridBuilder";
import { ChefWidgetLiveBody } from "@/components/widget-bodies/ChefWidgetLiveBody";

export default function ChefDashboard() {
  const [d, setD] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [widgetsData, setWidgetsData] = useState(null);

  useEffect(() => {
    api.get("/chef/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/chef/widgets/catalog").then((r) => setCatalog(r.data.widgets || [])).catch(() => {});
    api.get("/chef/widgets/preferences").then((r) => setPrefs(r.data.preferences || {})).catch(() => {});
    api.get("/chef/widgets/data").then((r) => setWidgetsData(r.data)).catch(() => {});
  }, []);

  const ent = d?.entreprise;
  const status = ent?.status;

  const enabledMap = React.useMemo(() => {
    const m = {};
    catalog.forEach((w) => { m[w.key] = !!prefs[w.key]?.enabled; });
    return m;
  }, [catalog, prefs]);
  const anyEnabled = Object.values(enabledMap).some(Boolean);

  return (
    <div className="min-h-full">
      <PageHeader
        eyebrow={`Entreprise · ${ent?.code || "—"}`}
        title="Tableau de bord"
        sub={ent ? `Bienvenue sur l'espace de ${ent.name}.` : ""}
        right={
          <Link to="/chef/dashboard-settings" data-testid="chef-dashboard-settings-btn" className="inline-flex items-center h-10 px-4 border border-[#DEE2E6] hover:border-[#0A0A0A] text-xs uppercase tracking-[0.2em] font-bold transition-colors">
            <Settings2 className="h-4 w-4 mr-2" /> Personnaliser
          </Link>
        }
      />

      {status === "pending_payment" && (
        <div className="mx-6 lg:mx-10 mt-6 p-5 border border-[#FCD34D] bg-[#FFFBEB]" data-testid="chef-payment-warning">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-[#92400E] mt-0.5" />
            <div className="text-sm text-[#92400E]">
              <div className="font-display font-bold mb-1">Paiement en attente</div>
              <p>Votre accès est actif. Sans régularisation sous 48h, l'accès sera bloqué.</p>
            </div>
          </div>
        </div>
      )}

      <div className="p-6 lg:p-10" data-testid="chef-widgets-grid">
        {!anyEnabled ? (
          <EmptyState />
        ) : (
          <WidgetGridBuilder
            catalog={catalog}
            enabledMap={enabledMap}
            prefs={prefs}
            readOnly
            testidPrefix="chef"
            renderBody={(widget) => <ChefWidgetLiveBody widget={widget} data={widgetsData?.[widget.key]} />}
          />
        )}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white border border-dashed border-[#DEE2E6] p-10 text-center" data-testid="chef-widgets-empty">
      <Settings2 className="h-10 w-10 mx-auto text-[#868E96] mb-3" />
      <div className="font-display font-bold text-lg mb-1">Votre dashboard est vide</div>
      <p className="text-sm text-[#868E96] mb-4">Activez au moins un widget pour personnaliser votre vue.</p>
      <Link to="/chef/dashboard-settings" data-testid="chef-widgets-empty-cta" className="inline-flex items-center h-10 px-5 bg-[#0A0A0A] text-white text-xs uppercase tracking-[0.2em] font-bold">
        Personnaliser mon dashboard
      </Link>
    </div>
  );
}
